/**
 * 抽壳特征提交执行处理器。
 */

import { ShellFeatureDefinitionModel } from '../../../definition/shell/ShellFeatureDefinition';
import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { ShellFeaturePrototype } from '../../../prototype/shell/ShellFeaturePrototype';
import type { FeatureExecutionRuntime } from '../../FeatureExecutionRuntime';
import {
    FeatureCommitExecutionHandler,
    type FeatureCommitExecutionInput,
    type FeatureCommitExecutionResult,
} from '../FeatureCommitExecutionHandler';
import { ShellCommitExecution } from './ShellCommitExecution';

/**
 * 抽壳特征提交执行处理器。
 */
export class ShellCommitExecutionHandler extends FeatureCommitExecutionHandler {
    /** 抽壳提交执行器。 */
    private readonly execution: ShellCommitExecution;
    /** 特征执行运行时。 */
    private readonly runtime: FeatureExecutionRuntime;

    /**
     * @param execution 抽壳提交执行器。
     * @param runtime 执行运行时。
     */
    public constructor(execution: ShellCommitExecution, runtime: FeatureExecutionRuntime) {
        super();
        this.execution = execution;
        this.runtime = runtime;
    }

    /**
     * @inheritdoc
     */
    public supports(input: FeatureCommitExecutionInput): boolean {
        return input.featureKind === 'shell' && input.definition instanceof ShellFeatureDefinitionModel;
    }

    /**
     * @inheritdoc
     * @throws 当提交输入中的定义不是抽壳特征定义时。
     */
    public async execute(input: FeatureCommitExecutionInput): Promise<FeatureCommitExecutionResult> {
        if (!(input.definition instanceof ShellFeatureDefinitionModel)) {
            throw new Error('抽壳提交处理器收到的 definition 类型不正确');
        }

        const featureId = input.featureId ?? `feature:${Date.now()}`;
        const prototype = new ShellFeaturePrototype({
            featureId,
            definition: input.definition,
            bindings: input.bindings,
            sourceSketchId: null,
        });

        if (input.session) {
            const replaced = await this.execution.commitEdit(
                input.session as FeatureEditSession<ShellFeatureDefinitionModel, ShellFeaturePrototype>,
                prototype,
            );
            return {
                replaced,
                featureId,
                prototype,
                meshData: null,
            };
        }

        const objectId = await this.execution.commitCreate(prototype);
        const meshData = await this.runtime.generateMeshData(objectId);
        return {
            replaced: false,
            featureId,
            prototype,
            meshData,
            objectId,
        };
    }
}
